import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  forwardRef,
} from 'react';
import '../../../../DNA/CreativePerformance/VisualsWrapper.scss';
import { EmptyState } from '../../../../DNA/CreativePerformance/EmptyState';
import { useWindowSize } from '../../../../../hooks/useWindowSize';
import Tooltip from '../../../../../components/Tooltip';
import { useOverflowActive } from '../../../../../hooks/useOverflowActive';
import {
  formatMetric,
  getVisualsPerPage,
} from '../../../../DNA/CreativePerformance/utils';
import Button from '../../../../../components/Button';
import { SortDropdown } from '../../../../DNA/CreativePerformance/SortOptions';
import { TopSpendingIndicator } from '../../../../DNA/CreativePerformance/Creative';
import {
  DARWIN_NAVY,
  DARWIN_NAVY_2,
  DARWIN_NAVY_3,
  DARWIN_NAVY_5,
} from '../../../../../constants';
import { useUrlParams } from '../../../../CreativeAnalytics/hooks/useUrlParams';
import { useGroupByContext } from '../../../contexts/GroupByContext';
import { VirtuosoGrid } from 'react-virtuoso';
import { useAnalyticsUrlProps } from '../../../../CreativeAnalytics/PivotReport/hooks';
import { useSelector } from 'react-redux';
import { getColumns } from '../SummaryTable';
import { formatMetricValue } from './utils/formatUtils';
import UploadFormHandler from '../../../../CreativeStudio/UploadFormHandler';
import { ACCEPT } from '../../../../CreativeStudio/constants';
import { useUploadReportBuilderAssetApi } from '../../../api/upload-asset';
import { Tag } from '../../../../../components/Tags';
import { OverridesSelectWrapper } from './Overrides';
import clsx from 'clsx';
import Pagination from '@mui/lab/Pagination';

const sortDetailData = (data = [], sortBy, desc) => {
  return [...data].sort((a, b) => {
    const valA = a[sortBy] ?? a[`total_${sortBy}`] ?? 0;
    const valB = b[sortBy] ?? b[`total_${sortBy}`] ?? 0;

    const effectiveDesc = sortBy.includes('roas') ? !desc : desc;

    return effectiveDesc ? valB - valA : valA - valB;
  });
};

interface CreativePerformanceProps {
  data?: any[];
  columnOptions: any;
  summaryCols: any;
  sort: any;
  selectedSummaryColumns: any;
  setSort: any;
  visual_id_data?: any[];
  master_mappings: any;
  handleUpload: any;
  onVisualClick?: (visual: any) => void;
}

export const CreativePerformance: React.FC<CreativePerformanceProps> = ({
  data = [],
  columnOptions,
  summaryCols,
  sort,
  selectedSummaryColumns,
  setSort,
  visual_id_data = [],
  master_mappings,
  handleUpload,
  visualsPerPageMultiplier,
  selectedLabel,
  onVisualClick,
}) => {
  const { reportBuilderGroupBy } = useGroupByContext() ?? {
    reportBuilderGroupBy: 'ad_name',
  };
  const dataset = useMemo(
    () =>
      reportBuilderGroupBy === 'visual_id' ? [...visual_id_data] : [...data],
    [reportBuilderGroupBy, visual_id_data, data]
  );

  const sorted = useMemo(() => {
    if (!sort?.sortBy) return dataset;

    const sortedData = [...dataset];

    return sortedData.sort((a, b) => {
      const sortBy = sort.sortBy;
      const desc = sort.desc;

      const valA = a[sortBy] ?? a[`total_${sortBy}`] ?? 0;
      const valB = b[sortBy] ?? b[`total_${sortBy}`] ?? 0;

      const effectiveDesc = sortBy.includes('roas') ? !desc : desc;

      return effectiveDesc ? valB - valA : valA - valB;
    });
  }, [dataset, sort]);

  return (
    <div>
      <p style={{ color: DARWIN_NAVY_3 }}>{sorted.length} visuals</p>
      <div
        className="creative-performance"
        style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
      >
        <VisualsWrapper
          visuals={sorted}
          columns={summaryCols}
          columnOptions={columnOptions}
          allowOverrides={false}
          master_mappings={master_mappings}
          selectedSummaryColumns={selectedSummaryColumns}
          handleUpload={handleUpload}
          visualsPerPageMultiplier={visualsPerPageMultiplier}
          selectedLabel={selectedLabel}
          onVisualClick={onVisualClick}
        />
      </div>
    </div>
  );
};

export const SortToggle = ({ sort, setSort }) => {
  const [show, setShow] = useState(false);
  const isSorted = !!sort?.sortBy?.length;
  const { primary_metric } = useUrlParams();

  const handleSort = (e, desc) => {
    let metric = e;

    if (e === 'cpa') metric = 'total_cpa';
    if (e === 'spend') metric = 'total_spend';

    setSort({
      sortBy: metric,
      desc,
    });
  };

  const SortText = (
    <p
      style={{
        fontWeight: 500,
        marginBottom: 0,
        marginLeft: '1rem',
      }}
      className="sort-text"
    >
      {isSorted && (
        <i
          className={`fa-solid fa-caret${
            sort?.sortBy?.includes('roas')
              ? sort?.desc
                ? '-up'
                : '-down'
              : sort?.desc
              ? '-down'
              : '-up'
          } mr-2`}
        />
      )}
      {!isSorted && (
        <i className="fa-solid fa-minus mr-2" style={{ color: '#616161' }} />
      )}
      {isSorted ? (
        <span>Sorted by {formatMetric(sort?.sortBy)} </span>
      ) : (
        <span style={{ color: '#616161' }}>No sort applied</span>
      )}
      {isSorted && <span>{formatMetric(sort?.sortOrder)}</span>}
    </p>
  );

  return (
    <div className="sort-options">
      <Button appearance="raised" onClick={() => setShow((prev) => !prev)}>
        {SortText}
      </Button>
      <SortDropdown
        show={show}
        setShow={setShow}
        setSort={handleSort}
        sortDesc={false}
        primary_metric={primary_metric}
      />
    </div>
  );
};

const useForceHeight = () => {
  const [height, setHeight] = useState(0);
  const measureRef = useRef(null);

  useLayoutEffect(() => {
    const measure = () => {
      if (measureRef.current) {
        const rect = measureRef.current.getBoundingClientRect();
        const parentRect =
          measureRef.current.parentElement?.getBoundingClientRect();

        if (parentRect) {
          const availableHeight = parentRect.height || window.innerHeight;
          setHeight(availableHeight);
        }
      }
    };

    measure();
    window.addEventListener('resize', measure);

    const timeout = setTimeout(measure, 100);

    return () => {
      window.removeEventListener('resize', measure);
      clearTimeout(timeout);
    };
  }, []);

  return { measureRef, height };
};

const GridList = forwardRef<HTMLDivElement, any>(
  ({ children, style, ...props }, ref) => (
    <div
      ref={ref}
      {...props}
      className="dna-grid"
      style={{
        ...style,
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
        gap: '1rem',
        padding: '1rem',
        boxSizing: 'border-box',
      }}
    >
      {children}
    </div>
  )
);

const GridScroller = forwardRef<HTMLDivElement, any>(
  ({ style, ...props }, ref) => (
    <div
      ref={ref}
      {...props}
      style={{
        ...style,
        height: '100%',
        position: 'relative',
        overflow: 'auto',
      }}
    />
  )
);

const GridFooter = () => <div style={{ height: '100px' }} />;

const GRID_COMPONENTS = {
  Item: 'div' as const,
  List: GridList,
  Scroller: GridScroller,
  Footer: GridFooter,
};

type VisualProps = {
  asset_url;
  visual_type;
  _id;
  id;
  thumbnail_url;
};

export const VisualsWrapper = ({
  visuals = [],
  columns = [],
  columnOptions,
  allowOverrides,
  zoomOnHover = false,
  onOverrideChange,
  onOverrideSave,
  onOverrideCancel,
  unsavedOverrides = new Set(),
  master_mappings,
  selectedSummaryColumns,
  handleUpload,
  overrideProps,
  visualsPerPageMultiplier = 1,
  selectedLabel,
  onVisualClick,
}) => {
  const { width } = useWindowSize();

  const { measureRef, height } = useForceHeight();

  const visualsPerPage = getVisualsPerPage(width, visualsPerPageMultiplier);
  const [currentPage, setCurrentPage] = useState(1);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const numOfPages = Math.ceil(visuals.length / visualsPerPage);

  const startIndex = (currentPage - 1) * visualsPerPage;
  const visibleData = visuals.slice(startIndex, startIndex + visualsPerPage);

  useEffect(() => {
    if (numOfPages > 0) {
      if (currentPage > numOfPages) {
        setCurrentPage(numOfPages);
      }

      if (currentPage < 1) {
        setCurrentPage(1);
      }
    } else {
      setCurrentPage(0);
    }
  }, [visuals, visualsPerPage, currentPage]);

  const stableOnOverrideChange = useCallback(
    (visualId: string) => {
      if (onOverrideChange) onOverrideChange(visualId);
    },
    [onOverrideChange]
  );

  const stableOnOverrideSave = useCallback(
    (visualId: string) => {
      if (onOverrideSave) onOverrideSave(visualId);
    },
    [onOverrideSave]
  );

  const stableOnOverrideCancel = useCallback(
    (visualId: string) => {
      if (onOverrideCancel) onOverrideCancel(visualId);
    },
    [onOverrideCancel]
  );

  if (visuals.length < 1)
    return <EmptyState text="Select a row above to view detailed stats." />;

  return (
    <div
      className="dna-grid-wrapper"
      style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      <div className="mb-3">
        <Pagination
          onChange={(e, value) => handlePageChange(value)}
          count={numOfPages}
          size="small"
        />
      </div>

      <div
        className="dna-grid-container"
        style={{
          flex: '1 1 auto',
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: '1rem',
        }}
      >
        <div className="dna-grid" key={`page-${selectedLabel}-${currentPage}}`}>
          {visibleData.map((visual, index) => {
            const globalIndex = startIndex + index;
            const isUnsavedChange = unsavedOverrides.has(
              visual.darwin_visual_id
            );

            const uniqueKey = `${selectedLabel}-${currentPage}-${
              visual.darwin_visual_id || visual._id
            }`;

            return (
              <VisualGridItem
                key={uniqueKey}
                visual={visual}
                index={globalIndex}
                isUnsavedChange={isUnsavedChange}
                allowOverrides={allowOverrides}
                columns={columns}
                columnOptions={columnOptions}
                zoomOnHover={zoomOnHover}
                master_mappings={master_mappings}
                selectedSummaryColumns={selectedSummaryColumns}
                handleUpload={handleUpload}
                overrideProps={overrideProps}
                onOverrideChange={stableOnOverrideChange}
                onOverrideSave={stableOnOverrideSave}
                onOverrideCancel={stableOnOverrideCancel}
                onVisualClick={onVisualClick}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

const VisualGridItem = React.memo(
  ({
    visual,
    index,
    isUnsavedChange,
    allowOverrides,
    columns,
    columnOptions,
    zoomOnHover,
    master_mappings,
    selectedSummaryColumns,
    handleUpload,
    overrideProps,
    onOverrideChange,
    onOverrideSave,
    onOverrideCancel,
    onVisualClick,
  }) => {
    const itemStyle = useMemo(
      () => ({
        position: 'relative' as const,
        marginBottom: allowOverrides ? '2rem' : '1rem',
      }),
      [isUnsavedChange, allowOverrides]
    );

    return (
      <div
        className={clsx('dna-grid-item', {
          'is-highlighted': isUnsavedChange,
        })}
        style={itemStyle}
        data-allow-overrides={allowOverrides}
        style={{ cursor: onVisualClick ? 'pointer' : 'default' }}
      >
        <Creative
          visual={visual}
          columns={columns}
          rank={visual?.rank}
          showRankings={!!visual?.rank}
          columnOptions={columnOptions}
          allowOverrides={allowOverrides}
          zoomOnHover={zoomOnHover}
          index={index}
          master_mappings={master_mappings}
          selectedSummaryColumns={selectedSummaryColumns}
          handleUpload={handleUpload}
          onClick={() => onVisualClick?.(visual)}
        />

        {allowOverrides && overrideProps && (
          <div className="p-3" style={{ background: '#fff' }}>
            <OverridesSelectWrapper
              key={`override-${visual.darwin_visual_id}`}
              visual={visual}
              current={overrideProps.current}
              options={overrideProps.options}
              handleOverride={overrideProps.handleOverride}
              loading={overrideProps.loading}
              onOverrideChange={() => onOverrideChange(visual.darwin_visual_id)}
              onOverrideSave={() => onOverrideSave(visual.darwin_visual_id)}
              onOverrideCancel={() => onOverrideCancel(visual.darwin_visual_id)}
              hasUnsavedChanges={isUnsavedChange}
            />
          </div>
        )}
      </div>
    );
  }
);

VisualGridItem.displayName = 'VisualGridItem';

export const Creative = ({
  visual,
  columns = [],
  rank,
  showRankings,
  columnOptions,
  overridesSelect,
  allowOverrides,
  zoomOnHover,
  index,
  onOverrideChange,
  onOverrideSave,
  master_mappings,
  selectedSummaryColumns,
  handleUpload,
  overrideContent,
  onClick,
}) => {
  return (
    <div className="dna-creative" onClick={onClick}>
      <div className="dna-creative__top-banner">
        {visual.isTopSpending && <TopSpendingIndicator />}
      </div>

      <Visual
        visual={visual}
        rank={rank}
        showRankings={showRankings}
        zoomOnHover={zoomOnHover}
        index={index}
        handleUpload={handleUpload}
      />
      {/* <Toggle display={display} setDisplay={setDisplay} /> */}
      <div className="dna-creative-details">
        <MetricData
          visual={visual}
          columns={columns}
          formatMetric={formatMetric}
          columnOptions={columnOptions}
          master_mappings={master_mappings}
          selectedSummaryColumns={selectedSummaryColumns}
        />
      </div>

      {allowOverrides && overrideContent && (
        <div className="p-3" style={{ background: '#fff' }}>
          {overrideContent}
        </div>
      )}
    </div>
  );
};

const Visual = ({
  visual,
  rank,
  showRankings,
  thumbnails_only,
  zoomOnHover,
  index,
  handleUpload,
}) => {
  let VisualDisplay = null;
  let EnlargedVisualDisplay = null;

  const uploadReportBuilderAssetApi = useUploadReportBuilderAssetApi();

  const handleOnChange = useCallback(async (files) => {
    const type = files[0].type;
    const reader = new FileReader();
    reader.readAsDataURL(files[0]);
    // reader.onload = function () {
    //   const ad_id = visual['Ad ID'] || visual.array_of_ad_ids[0];
    //   handleUpload(ad_id, reader.result, type);
    // };
    reader.onerror = function (error) {
      console.log('Error: ', error);
    };

    const ad_id = visual.darwin_visual_id || visual.array_of_ad_ids[0];
    const fileName = `frame_${ad_id}`;

    const { s3_filename, s3_url, versionId } =
      await uploadReportBuilderAssetApi.upload(files, fileName);

    handleUpload(ad_id, s3_url, type);
  }, []);

  if (
    !visual.s3_url ||
    !visual.visual_type ||
    visual.visual_type === 'Unknown'
  ) {
    return (
      <div className="img-wrapper p-4" style={{ background: DARWIN_NAVY }}>
        <p className="text-white">
          <i
            className="fa-solid fa-circle-exclamation mr-2"
            style={{ opacity: 0.5 }}
          ></i>
          Deleted Visual or Missing Permissions
        </p>
        <UploadFormHandler
          handleFiles={handleOnChange}
          multiple={false}
          accept={ACCEPT}
        >
          {(onClick) => (
            <div className="upload-asset" onClick={onClick}>
              <i className="fa fa-upload header-icon"></i>
            </div>
          )}
        </UploadFormHandler>
      </div>
    );
  }

  if (visual.visual_type.toLowerCase() === 'video') {
    VisualDisplay = (
      <video
        key={visual.s3_url}
        autoPlay={false}
        loop
        muted
        plays-inline="true"
        controls
      >
        <source src={visual.s3_url} type="video/mp4" />
      </video>
    );
    EnlargedVisualDisplay = (
      <video
        key={`enlarged-${visual.s3_url}`}
        autoPlay={false}
        loop
        muted
        plays-inline="true"
        controls
        style={{
          width: '50vw',
          maxHeight: '50vh',
          objectFit: 'contain',
          backgroundColor: '#000',
        }}
      >
        <source src={visual.s3_url} type="video/mp4" />
      </video>
    );
  } else {
    VisualDisplay = (
      <img
        className="dna-creative__img"
        src={visual.s3_url}
        alt={visual.ad_name}
      />
    );
    EnlargedVisualDisplay = (
      <a href={visual.s3_url} target="_blank" rel="noopener noreferrer">
        <img
          src={visual.s3_url}
          alt={visual.ad_name}
          style={{
            width: '50vw',
            maxHeight: '50vh',
            objectFit: 'contain',
            backgroundColor: '#000',
          }}
        />
      </a>
    );
  }

  return (
    <div className="img-wrapper">
      <Tooltip
        style={{
          maxWidth: 'none',
        }}
        content={EnlargedVisualDisplay}
        follow={false}
        interactive
        maxWidth="none"
      >
        {VisualDisplay}
      </Tooltip>
      {showRankings && <div className="rank">{rank}</div>}
    </div>
  );
};

const MetricData = (props) => {
  return (
    <>
      <div className="mb-3">
        <Data {...props} />
      </div>
    </>
  );
};

const Data = ({
  visual,
  columns,
  columnOptions = [],
  master_mappings,
  selectedSummaryColumns,
}) => {
  const { reportBuilderGroupBy } = useGroupByContext() ?? {
    reportBuilderGroupBy: 'ad_name',
  };
  const shouldGroupByVisualId =
    reportBuilderGroupBy === 'visual_id' && !!visual.array_of_ad_names;

  const columnToMetricMap = useMemo(() => {
    if (!master_mappings || !Array.isArray(master_mappings)) return {};

    return master_mappings.reduce((acc, metric) => {
      acc[metric.og_metric] = metric;
      return acc;
    }, {});
  }, [master_mappings]);

  const cols = columns
    .filter((x) => selectedSummaryColumns.includes(x.metricName))
    .map((col) => {
      return (
        <DataItem
          key={`${visual.darwin_visual_id ?? visual.ad_name ?? ''}_${
            col.metricName
          }`}
          visual={visual}
          label={col.Header}
          value={col.metricName}
          metric={columnToMetricMap[col.metricName]}
        />
      );
    });

  if (cols.length % 2 !== 0) cols.push(<li key="empty"></li>);

  return (
    <div className="dna-creative-data dna-creative-data--grid">
      <ul className="mb-0">
        {cols}
        {visual.darwin_visual_id ? (
          <DataItem
            {...{ visual }}
            label="Visual ID"
            value={'darwin_visual_id'}
          />
        ) : null}
        <li className="data-item" key={`${visual._id}_ad-name`}>
          <Tooltip
            content={
              shouldGroupByVisualId ? (
                <ul style={{ listStyle: 'square', padding: '0.5rem' }}>
                  {visual.array_of_ad_names.map((name) => (
                    <li className="mb-3" key={name}>
                      {name}
                    </li>
                  ))}
                </ul>
              ) : (
                visual.ad_name ?? ''
              )
            }
            interactive
            follow={false}
          >
            <div className="w-100">
              <p className="data-item__label">
                Ad name{shouldGroupByVisualId ? '(s)' : ''}
              </p>
              <p
                style={{
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                className="data-item__value"
              >
                {shouldGroupByVisualId
                  ? visual.array_of_ad_names[0]
                  : visual.ad_name}
              </p>
            </div>
          </Tooltip>
        </li>
      </ul>
    </div>
  );
};

const DataItem = ({ visual, label, value, metric }) => {
  const [ref, overflowActive] = useOverflowActive();
  return (
    <li className="data-item" key={`${visual._id}_${value}`}>
      <Tooltip disabled={!overflowActive} content={formatMetric(label)}>
        <p className="data-item__label">{formatMetric(label)}</p>
      </Tooltip>
      <p className="data-item__value">
        {formatMetricValue(visual[value], metric)}{' '}
      </p>
    </li>
  );
};
